import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SurveysService,
  type QuestionRespons,
  type QuestionResponsCreate,
  type QuestionResponsUpdate,
} from "@/lib/api_client";
import { useAuth } from "@/lib/auth";
import { impersonateHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

// Anamnesis answers are patient health data, so these hooks run on the
// impersonation token the anamnesis page already holds, and that token rather
// than a filter is what scopes the rows server-side. It therefore has to be
// the token for the patient the caller asked about — otherwise a stale one
// would fill this cache key with someone else's answers.
const tokenFor = (
  wanted: string | null | undefined,
  impersonatingUserId: string | null,
  token: string | null,
) => (wanted && impersonatingUserId === wanted ? token : null);

export const useQuestionResponsesByUser = (
  authUserId: string | null | undefined,
) => {
  const { impersonateToken, impersonatingUserId } = useAuth();
  const token = tokenFor(authUserId, impersonatingUserId, impersonateToken);
  return useQuery({
    queryKey: ["question-responses", "by-user", authUserId],
    queryFn: () =>
      fetchAll<QuestionRespons>((opts) =>
        SurveysService.getApiQuestionResponses({
          ...opts,
          headers: impersonateHeaders(token!, opts.headers),
        }),
      ),
    enabled: !!token,
  });
};

export const useCreateQuestionResponse = () => {
  const qc = useQueryClient();
  const { impersonateToken, impersonatingUserId } = useAuth();
  return useMutation({
    mutationFn: async (data: QuestionResponsCreate) => {
      const token = tokenFor(
        data.authUserId,
        impersonatingUserId,
        impersonateToken,
      );
      if (!token) throw new Error("Ikke impersonert som denne pasienten");
      const res = await SurveysService.postApiQuestionResponses({
        body: data,
        headers: impersonateHeaders(token),
      });
      if (!res.data) throw new Error("Failed to create question response");
      return res.data as unknown as QuestionRespons;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["question-responses", "by-user", variables.authUserId],
      });
      // Also refresh the impersonated-view cache used by the survey panel
      qc.invalidateQueries({
        queryKey: ["impersonated-question-responses", variables.authUserId],
      });
    },
  });
};

export const useUpdateQuestionResponse = () => {
  const qc = useQueryClient();
  const { impersonateToken, impersonatingUserId } = useAuth();
  return useMutation({
    mutationFn: async ({
      id,
      authUserId,
      data,
    }: {
      id: string;
      authUserId: string;
      data: QuestionResponsUpdate;
    }) => {
      const token = tokenFor(authUserId, impersonatingUserId, impersonateToken);
      if (!token) throw new Error("Ikke impersonert som denne pasienten");
      const res = await SurveysService.putApiQuestionResponsesById({
        path: { id },
        body: data,
        headers: impersonateHeaders(token),
      });
      if (!res.data) throw new Error("Failed to update question response");
      return res.data as unknown as QuestionRespons;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["question-responses", "by-user", variables.authUserId],
      });
      qc.invalidateQueries({
        queryKey: ["impersonated-question-responses", variables.authUserId],
      });
    },
  });
};
